import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface WheelPickerProps {
  items: string[];
  value: string;
  onChange: (nextValue: string) => void;
  ariaLabel: string;
  className?: string;
  heightPx?: number;
  itemHeightPx?: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getIndexByScrollTop = (scrollTop: number, itemHeightPx: number, maxIndex: number) => {
  if (maxIndex <= 0) return 0;
  return clamp(Math.round(scrollTop / itemHeightPx), 0, maxIndex);
};

const getOpacityByDistance = (distance: number) => {
  if (distance <= 0) return 1;
  if (distance === 1) return 0.45;
  if (distance === 2) return 0.25;
  return 0.15;
};

const getScaleByDistance = (distance: number) => {
  if (distance <= 0) return 1;
  if (distance === 1) return 0.96;
  if (distance === 2) return 0.92;
  return 0.9;
};

export default function WheelPicker({
  items,
  value,
  onChange,
  ariaLabel,
  className = '',
  heightPx = 113,
  itemHeightPx = 31
}: WheelPickerProps) {
  const containerRef = useRef<HTMLFieldSetElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const scrollEndTimerRef = useRef<number | null>(null);

  const maxIndex = Math.max(items.length - 1, 0);
  const valueIndex = Math.max(items.indexOf(value), 0);
  const [activeIndex, setActiveIndex] = useState<number>(valueIndex);

  const paddingPx = useMemo(
    () => Math.max((heightPx - itemHeightPx) / 2, 0),
    [heightPx, itemHeightPx]
  );

  const scrollToIndex = useCallback(
    (index: number, behavior: ScrollBehavior) => {
      const el = containerRef.current;
      if (!el) return;
      el.scrollTo({ top: index * itemHeightPx, behavior });
    },
    [itemHeightPx]
  );

  useEffect(() => {
    setActiveIndex(valueIndex);
    scrollToIndex(valueIndex, 'auto');
  }, [scrollToIndex, valueIndex]);

  useEffect(() => {
    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      if (scrollEndTimerRef.current) window.clearTimeout(scrollEndTimerRef.current);
    };
  }, []);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;

    if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    rafRef.current = window.requestAnimationFrame(() => {
      const nextIndex = getIndexByScrollTop(el.scrollTop, itemHeightPx, maxIndex);
      setActiveIndex(nextIndex);
    });

    if (scrollEndTimerRef.current) window.clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = window.setTimeout(() => {
      const nextIndex = getIndexByScrollTop(el.scrollTop, itemHeightPx, maxIndex);
      const nextValue = items[nextIndex] ?? '';
      if (nextValue && nextValue !== value) onChange(nextValue);
      scrollToIndex(nextIndex, 'smooth');
    }, 120);
  };

  return (
    <div className={`relative ${className}`} style={{ height: heightPx }}>
      <div className="absolute inset-x-0 top-0 pointer-events-none bg-gradient-to-b from-pure-white to-transparent" />
      <div className="absolute inset-x-0 bottom-0 pointer-events-none bg-gradient-to-t from-pure-white to-transparent" />

      <fieldset
        ref={containerRef}
        className="hiddenScrollBar h-full overflow-y-auto overscroll-contain"
        style={{
          paddingTop: paddingPx,
          paddingBottom: paddingPx,
          scrollSnapType: 'y mandatory'
        }}
        onScroll={handleScroll}
      >
        <legend className="sr-only">{ariaLabel}</legend>
        {items.map((item, index) => {
          const distance = Math.abs(index - activeIndex);
          const isActive = index === activeIndex;
          const opacity = getOpacityByDistance(distance);
          const scale = getScaleByDistance(distance);

          return (
            <button
              key={item}
              type="button"
              aria-pressed={isActive}
              className="w-full flex items-center justify-center"
              style={{
                height: itemHeightPx,
                scrollSnapAlign: 'center',
                opacity,
                transform: `scale(${scale})`,
                transition: 'opacity 120ms ease, transform 120ms ease'
              }}
              onClick={() => {
                setActiveIndex(index);
                scrollToIndex(index, 'smooth');
                if (item !== value) onChange(item);
              }}
            >
              <span
                className={
                  isActive
                    ? 'text-text-main-lv1 text-[23px] leading-[27px]'
                    : 'text-text-main-lv3 text-[18px] leading-[21px]'
                }
              >
                {item}
              </span>
            </button>
          );
        })}
      </fieldset>
    </div>
  );
}
